import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { fetchWrapper } from '../helpers';
import { API_ENDPOINT } from '../constants';

import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  IEmailCronAuthArg,
  IEmailCronConfig,
  IEmailCronConfigState,
  IEmailCronCreateArg,
  IEmailCronIdArg,
  IEmailCronPreviewArg,
  IEmailCronUpdateArg,
} from '../utils/types';

const name = 'emailCronConfigs';
const baseUrl = `${API_ENDPOINT}/email_cron_configs`;

const initialState: IEmailCronConfigState = {
  list: [],
  current: null,
  requestedConfigId: null,
  preview: null,
  previewConfigId: null,
  loading: false,
};

const getList = createAsyncThunk(
  `${name}/getList`,
  async ({ getAccessTokenSilently }: IEmailCronAuthArg) =>
    fetchWrapper.get({ url: `${baseUrl}/`, getAccessTokenSilently }),
);

const getById = createAsyncThunk(
  `${name}/getById`,
  async ({ id, getAccessTokenSilently }: IEmailCronIdArg) =>
    fetchWrapper.get({ url: `${baseUrl}/${id}/`, getAccessTokenSilently }),
);

const create = createAsyncThunk(
  `${name}/create`,
  async ({ data, getAccessTokenSilently }: IEmailCronCreateArg) =>
    fetchWrapper.post({ url: `${baseUrl}/`, getAccessTokenSilently }, data),
);

const update = createAsyncThunk(
  `${name}/update`,
  async ({ id, data, getAccessTokenSilently }: IEmailCronUpdateArg) =>
    fetchWrapper.patch({ url: `${baseUrl}/${id}/`, getAccessTokenSilently }, data),
);

const remove = createAsyncThunk(
  `${name}/remove`,
  async ({ id, getAccessTokenSilently }: IEmailCronIdArg) =>
    fetchWrapper.delete({ url: `${baseUrl}/${id}/`, getAccessTokenSilently }),
);

const preview = createAsyncThunk(
  `${name}/preview`,
  async ({ id, data, getAccessTokenSilently }: IEmailCronPreviewArg) =>
    fetchWrapper.post({ url: `${baseUrl}/${id}/preview/`, getAccessTokenSilently }, data),
);

const slice = createSlice({
  name,
  initialState,
  reducers: {
    clearPreview: (state) => {
      state.preview = null;
      state.previewConfigId = null;
    },
    clearCurrent: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getList.fulfilled, (state, action: PayloadAction<IEmailCronConfig[]>) => {
        state.list = action.payload;
        state.loading = false;
      })
      .addCase(getById.pending, (state, action) => {
        // Record which config the user requested and clear any stale `current`
        // immediately so a previously-viewed config can't flash under this URL.
        state.requestedConfigId = String(action.meta.arg.id);
        state.current = null;
      })
      .addCase(getById.fulfilled, (state, action) => {
        state.loading = false;
        // Only accept this response if it's for the config the user still wants.
        // A late response for a config the user navigated away from is ignored so
        // it can't overwrite the correct `current` (which would strand the loader).
        if (String(action.payload?.id) === String(state.requestedConfigId)) {
          state.current = action.payload;
        }
      })
      .addCase(create.fulfilled, (state) => {
        state.loading = false;
        toast.success('Email cron config created');
      })
      .addCase(update.fulfilled, (state, action: PayloadAction<IEmailCronConfig>) => {
        state.current = action.payload;
        // Keep the cached list (the Dashboard's Email Reports tab, which only
        // refetches when empty) in sync so it doesn't show stale data after a save.
        state.list = state.list.map((c) => (c.id === action.payload.id ? action.payload : c));
        state.loading = false;
        toast.success('Email cron config updated');
      })
      .addCase(remove.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted item from the cached list so the Dashboard's
        // Email Reports tab (which only refetches when the list is empty)
        // doesn't show the deleted row when we navigate back to it.
        const deletedId = action.meta.arg.id;
        state.list = state.list.filter((c) => String(c.id) !== String(deletedId));
        toast.success('Email cron config deleted');
      })
      .addCase(preview.fulfilled, (state, action) => {
        state.preview = action.payload;
        // Tag the preview with the config it was generated for so the page can
        // refuse to render a late preview from a different config.
        state.previewConfigId = String(action.meta.arg.id);
        state.loading = false;
      })
      .addMatcher(
        (action) => action.type.startsWith(name) && action.type.endsWith('/pending'),
        (state) => {
          state.loading = true;
        },
      )
      .addMatcher(
        (action) => action.type.startsWith(name) && action.type.endsWith('/rejected'),
        // RTK hands a plain matcher action here; the SerializedError shape is all we read.
        (state, action: { error?: { code?: string; message?: string } }) => {
          state.loading = false;
          // fetchWrapper already shows a global toast for 403/502/503/504, so
          // skip those here to avoid double-toasting. Everything else (e.g.
          // 400/422, network errors) is still surfaced exactly once.
          const FETCH_WRAPPER_TOASTED = ['403', '502', '503', '504'];
          if (!FETCH_WRAPPER_TOASTED.includes(String(action.error?.code))) {
            toast.error(action.error?.message || 'Something went wrong');
          }
        },
      );
  },
});

export const emailCronConfigActions = {
  ...slice.actions,
  getList,
  getById,
  create,
  update,
  remove,
  preview,
};
export const emailCronConfigReducer = slice.reducer;
